/**
 * URL Utilities
 * Shared URL parsing and domain extraction functions
 */

/**
 * Extracts the domain/hostname from a URL string
 * Returns 'direct' for empty URLs, invalid URLs, or direct traffic
 *
 * @param url - The URL string to parse
 * @returns The hostname (e.g., 'www.google.com') or 'direct'
 *
 * @example
 * extractDomain('https://www.google.com/search') // => 'www.google.com'
 * extractDomain('') // => 'direct'
 * extractDomain('invalid-url') // => 'direct'
 */
export const extractDomain = (url: string): string => {
  if (!url) return 'direct';

  try {
    return new URL(url).hostname;
  } catch {
    return 'direct';
  }
};

/**
 * Constants for domain extraction
 */
export const DOMAIN_EXTRACTION_CONSTANTS = {
  /** Fallback value for empty or invalid URLs */
  DIRECT_TRAFFIC: 'direct',
} as const;

/** Allow-listed URL schemes for affiliate banner links (blocks javascript:, data:, etc.) */
const SAFE_BANNER_SCHEMES = ['https:', 'http:'] as const;

/**
 * Checks if a URL is safe for affiliate dashboard banner links.
 * Internal links must start with /. External links must use https: or http: only.
 *
 * @param url - The URL string to validate
 * @param linkType - 'internal' (in-app path) or 'external' (opens in new tab)
 * @returns true if the URL is safe to use
 */
export const isSafeBannerUrl = (
  url: string | undefined,
  linkType: 'internal' | 'external',
): boolean => {
  const trimmed = url?.trim();
  if (!trimmed) return true; // Empty is valid (optional field)

  if (linkType === 'internal') {
    return trimmed.startsWith('/') && !trimmed.includes(':');
  }

  try {
    const parsed = new URL(trimmed);
    return SAFE_BANNER_SCHEMES.includes(
      parsed.protocol as (typeof SAFE_BANNER_SCHEMES)[number],
    );
  } catch {
    return false;
  }
};

/**
 * Returns a safe href for display in anchor tags, or undefined if the URL is not safe.
 *
 * @param url - The URL string
 * @param linkType - 'internal' or 'external'
 * @returns Safe href string or undefined
 */
export const getSafeBannerHref = (
  url: string | undefined,
  linkType: 'internal' | 'external',
): string | undefined => {
  const trimmed = url?.trim();
  if (!trimmed) return undefined;
  if (!isSafeBannerUrl(trimmed, linkType)) return undefined;
  return trimmed;
};
